import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  input,
  Input,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatTabGroup } from '@angular/material/tabs';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import {
  CategorieUm,
  UnitaDiMisura,
  UnitaDiMisuraService,
} from 'src/app/services/unita-di-misura/unita-di-misura.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { EditTabellaDinamicaBottomSheetComponent } from './edit-tabella-dinamica-bottom-sheet/edit-tabella-dinamica-bottom-sheet.component';

export interface TabellaDinamica {
  id: string | undefined;
  colonne: Colonna[];
  righe: Riga[];
  totaleRighe: boolean;
  totaleColonne: boolean;
  titoloTabella: string;
}

export interface Colonna {
  id: string;
  label: string;
  idCatUm: string | undefined;
  tipoColonna: 'TESTO' | 'BOOLEAN' | 'UM' | undefined;
  unitaDiMisura: UnitaDiMisura | undefined;
}

export interface Riga {
  id: string;
  label: string;
  celle: Cella;
}

export interface Cella {
  [rowId: string]: {
    [colId: string]: string | number | null;
  };
}

@Component({
  selector: 'app-tabella-cfg-dinamica',
  standalone: false,

  templateUrl: './tabella-cfg-dinamica.component.html',
  styleUrl: './tabella-cfg-dinamica.component.scss',
})
export class TabellaCfgDinamicaComponent implements AfterViewInit {
  @ViewChildren('rowTextareaEl') rowTextareaEls!: QueryList<ElementRef>;
  @ViewChildren('colTextareaEl') colTextareaEls!: QueryList<ElementRef>;

  @ViewChild('tabRigheColonne') tabRigheColonne!: MatTabGroup;

  @ViewChildren('inputDesc', { read: CdkTextareaAutosize })
  queryListTextarea!: QueryList<CdkTextareaAutosize>;

  @Output() variazioneDimensioneCelle: EventEmitter<boolean> = new EventEmitter();
  @Output() salvataggioTabella: EventEmitter<TabellaDinamica> = new EventEmitter();

  private _tabella: TabellaDinamica | undefined = undefined;

  @Input()
  set tabella(tabella: TabellaDinamica | undefined) {
    this._tabella = tabella;
  }

  get tabella() {
    return this._tabella;
  }

  private _isConfigurazione: boolean = false;
  @Input()
  set isConfigurazione(isConfigurazione: boolean) {
    this._isConfigurazione = isConfigurazione;
  }

  get isConfigurazione() {
    return this._isConfigurazione;
  }

  private _disabilitaHeaderRC: boolean = false;
  @Input()
  set disabilitaHeaderRC(disabilitaHeaderRC: boolean) {
    this._disabilitaHeaderRC = disabilitaHeaderRC;
  }

  get disabilitaHeaderRC() {
    return this._disabilitaHeaderRC;
  }

  private _disabilitaCelle: boolean = false;
  @Input()
  set disabilitaCelle(disabilitaCelle: boolean) {
    this._disabilitaCelle = disabilitaCelle;
  }

  get disabilitaCelle() {
    return this._disabilitaCelle;
  }

  private _nascondiBottoni: boolean = false;
  @Input()
  set nascondiBottoni(nascondiBottoni: boolean) {
    this._nascondiBottoni = nascondiBottoni;
  }

  get nascondiBottoni() {
    return this._nascondiBottoni;
  }

  private _temporaryToken: string | undefined = undefined;
  @Input()
  set temporaryToken(temporaryToken: string | undefined) {
    this._temporaryToken = temporaryToken;
  }

  get temporaryToken() {
    return this._temporaryToken;
  }

  readonly isRequired = input<boolean>(false);

  @Output() tabellaChange = new EventEmitter<TabellaDinamica>();

  public infiniteRecords: number = Infinity;

  public tipologiaSelezionata: 'TESTO' | 'BOOLEAN' | 'UM' | undefined = undefined;
  public tempColonna: string = '';
  public tempRiga: string = '';
  public categoriaUnitaSelezionata: CategorieUm | undefined = undefined;
  public selectedUnitaDiMisura: UnitaDiMisura | undefined = undefined;
  public arrayCategorie: CategorieUm[] = [];

  constructor(
    private readonly utilityService: UtilityService,
    private readonly _unitaDiMisuraService: UnitaDiMisuraService,
    private readonly _bottomSheet: MatBottomSheet,
    private readonly cdRef: ChangeDetectorRef,
  ) {}

  public arrayTipologiaColonna = [
    {
      chiave: 'TESTO',
      valore: 'Testuale',
    },
    {
      chiave: 'BOOLEAN',
      valore: 'Si / No',
    },
    {
      chiave: 'UM',
      valore: 'Unità di Misura',
    },
  ];

  displayedColumns: string[] = [];

  colonne: Colonna[] = [];

  righe: Riga[] = [];

  totaleRighe: boolean = false;
  totaleColonne: boolean = false;
  titoloTabella: string = '';

  ngAfterViewInit() {
    this._getCategorie().subscribe();
    if (this.tabella) {
      this.colonne = this.tabella?.colonne ?? [];
      this.righe = this.tabella?.righe ?? [];
      this.totaleRighe = this.tabella?.totaleRighe ?? false;
      this.totaleColonne = this.tabella?.totaleColonne ?? false;
      this.titoloTabella = this.tabella.titoloTabella ?? '';
    }

    this.updateDisplayedColumns();

    /* Se sono in compilazione e ho una tabella di tipo normale (senza header sulle righe) allora aggiungo una riga vuota di campilazione */
    if (!this.isConfigurazione && this.nascondiPrimaColonna() && !this.tabella?.righe.length) {
      this.buildRigaVuota();
    }

    setTimeout(() => {
      this.callSincronizzaAltezza();

      this.queryListTextarea?.forEach((area) => area.resizeToFitContent(true));
    }, 0);

    this.cdRef.detectChanges();
  }

  // Quando c'è una modifica (es: edit celle)
  onModificaTabella(updatedData: TabellaDinamica | undefined) {
    this.tabella = updatedData;
    this.tabellaChange.emit(this.tabella);
  }

  /**
   * Mi ridimensiona l'altezza delle textarea sulle righe e celle
   * @param rigaId
   */
  sincronizzaAltezzaRiga(rigaId: string) {
    // setTimeout non necessario se non serve posticipare
    requestAnimationFrame(() => {
      const righe = this.rowTextareaEls.filter((el) => {
        const native = el.nativeElement as HTMLTextAreaElement;
        return native.dataset['rigaId'] === rigaId;
      });

      let maxHeight = 0;
      righe.forEach((elRef) => {
        const el = elRef.nativeElement as HTMLTextAreaElement;
        el.style.height = 'auto';
        if (el.scrollHeight > maxHeight) {
          maxHeight = el.scrollHeight;
        }
      });

      righe.forEach((elRef) => {
        const el = elRef.nativeElement as HTMLTextAreaElement;
        el.style.height = `${maxHeight}px`;
      });
    });
    this.variazioneDimensioneCelle.emit(true);
    /* this.onModificaTabella(this.salva()) */
  }

  /**
   *  Mi ridimensiona l'altezza delle textarea sulle colonne
   */
  sincronizzaAltezzaColonna() {
    // Posticipa al prossimo ciclo del browser (dopo il rendering)
    requestAnimationFrame(() => {
      let maxHeight = 0;

      // Calcola l'altezza massima
      this.colTextareaEls.forEach((colTextareaElsRef) => {
        const el = colTextareaElsRef.nativeElement as HTMLTextAreaElement;
        el.style.height = 'auto'; // resetta altezza per ottenere scrollHeight corretto
        if (el.scrollHeight > maxHeight) {
          maxHeight = el.scrollHeight;
        }
      });

      // Applica l'altezza massima a tutti
      this.colTextareaEls.forEach((colTextareaElsRef) => {
        const el = colTextareaElsRef.nativeElement as HTMLTextAreaElement;
        el.style.height = `${maxHeight}px`;
      });
    });
    this.variazioneDimensioneCelle.emit(true);
    /* this.onModificaTabella(this.salva()) */
  }

  rimuoviColonna(colonna: Colonna) {
    this.colonne = this.colonne.filter((c) => c.id !== colonna.id);
    for (const riga of this.righe) {
      delete riga.celle[riga.id][colonna.id];
    }

    this.updateDisplayedColumns();
    this.callSincronizzaAltezza();
  }

  rimuoviRiga(rigaDaRimuovere: Riga): void {
    this.righe = this.righe.filter((riga) => riga.id !== rigaDaRimuovere.id);
    this.callSincronizzaAltezza();
  }

  updateDisplayedColumns() {
    if (!this.isConfigurazione && this.nascondiPrimaColonna() && this.mostraELimina) {
      this.displayedColumns = ['azione', 'rowLabel', ...this.colonne.map((col) => col.id)];
    } else {
      this.displayedColumns = ['rowLabel', ...this.colonne.map((col) => col.id)];
    }
  }

  /**
   * Metodo che prende le categorie
   */
  private _getCategorie() {
    let obs;
    if (this._temporaryToken) {
      obs = this._unitaDiMisuraService.getUnitaDiMisuraConTempToken(
        this._temporaryToken,
        0,
        1000,
        '',
        [],
      );
    } else {
      obs = this._unitaDiMisuraService.getCategoria();
    }

    return obs.pipe(
      map((resultCategoria: CategorieUm[]) => {
        this.arrayCategorie = resultCategoria || [];

        return true;
      }),
      catchError((err: any) => {
        console.error('Errore durante il caricamento delle Categorie:', err);

        this.utilityService.openDialog({
          titolo: 'Attenzione',
          descrizione: 'Errore nel recupero delle Categorie',
          bottoni: [{ nome_btn: 'Ok' }],
        });
        return of(false);
      }),
    );
  }

  /**
   * Genera un id casuale per aggiunta di riga e colonna
   * @param prefix r = riga, c = colonna
   * @returns
   */
  public generateId(prefix: string): string {
    return prefix + Date.now().toString();
  }

  /**
   * Metodo di aggiunta colonna nella tabella
   */
  async aggiungiColonna() {
    const result = await this.configuraRigheColonne('COLONNA');
    const colonna: Colonna = result.colonna;

    if (!colonna.label) {
      return;
    }

    const newId = this.generateId('c');
    const nuovaColonna: Colonna = {
      id: newId,
      label: colonna.label,
      idCatUm: colonna.idCatUm,
      tipoColonna: colonna.tipoColonna,
      unitaDiMisura: colonna.unitaDiMisura,
    };

    this.colonne.push(nuovaColonna);
    for (const riga of this.righe) {
      riga.celle[riga.id][newId] = null;
    }
    this.updateDisplayedColumns();
    this.utilityService.openSnackBar(' Inserita colonna nella tabella ', '', {
      duration: 2000,
      panelClass: ['success-snackbar'],
    });

    this.callSincronizzaAltezza();
  }

  /**
   * Metodo di aggiunta riga nella tabella
   */
  async aggiungiRiga() {
    await this.buildRigaVuota();

    this.utilityService.openSnackBar(' Inserita riga nella tabella ', '', {
      duration: 2000,
      panelClass: ['success-snackbar'],
    });
  }

  /**
   * Construzione di una riga vuota
   * @returns
   */
  private async buildRigaVuota() {
    let nuovaRiga: Riga;
    const newId = this.generateId('r');

    /* Se nessuna riga ha una label, allora vuoldire che la tabella è stata configurata senza intestazioni sulle righe
    Quindi permetto di inserire righe vuote senza intestazioni, per il COMPILATORE */
    if (!this.isConfigurazione && this.righe.every((r) => !r.label)) {
      //Sono in compilazione di una tabella senza intestazioni sulle righe
      nuovaRiga = {
        celle: {},
        id: newId,
        label: '',
      };
    } else {
      /* Mi trovo in configurazione e quindi permetto di aggiungere righe con intestazioni */
      const result = await this.configuraRigheColonne('RIGA');
      const riga: Riga = result.riga;

      if (!riga.label) {
        return;
      }

      nuovaRiga = { id: newId, label: riga.label, celle: {} };
    }

    nuovaRiga.celle[newId] = {};

    for (const col of this.colonne) {
      nuovaRiga.celle[newId][col.id] = null;
    }

    this.righe = [...this.righe, nuovaRiga]; // <-- assegna un nuovo array per triggerare la tabella

    // Ridimensiono tutte le righe / celle
    this.callSincronizzaAltezza();
  }

  /**
   * Bottomsheet dove si possono modificare le righe e le colonne
   * @param tipo 'COLONNA' o 'RIGA'
   * @param elemento riga o colonna da modificare
   * @returns
   */
  async configuraRigheColonne(tipo?: string, elemento?: any): Promise<any> {
    try {
      const dialogRef = this._bottomSheet.open(EditTabellaDinamicaBottomSheetComponent, {
        data: {
          elemento: elemento,
          tipo: tipo,
          arrayCategorie: this.arrayCategorie,
        },
        disableClose: true,
      });

      const result = await firstValueFrom(dialogRef.afterDismissed());
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Metodo per modificare l'header delle colonne e righe, quando clicco mi si apre una bottomsheet per modificarle
   * @param tipo 'COLONNA' o 'RIGA'
   * @param elemento riga o colonna da modificare
   * @returns
   */
  async modificaHeader(tipo: string, elemento: any) {
    if (!this.isConfigurazione) {
      return;
    }

    switch (tipo) {
      case 'COLONNA': {
        const result = await this.configuraRigheColonne(tipo, elemento);

        const colonna: Colonna = result.colonna;

        if (result.isElimina) {
          this.rimuoviColonna(colonna);

          return;
        }

        if (colonna) {
          this.colonne = this.colonne.map((col) => (col.id === colonna.id ? colonna : col));
          this.sincronizzaAltezzaColonna();
        }

        break;
      }
      case 'RIGA': {
        const result = await this.configuraRigheColonne(tipo, elemento);
        const riga: Riga = result.riga;

        if (result.isElimina) {
          this.rimuoviRiga(riga);

          return;
        }

        if (riga) {
          this.righe = this.righe.map((rig) => (rig.id === riga.id ? riga : rig));

          this.sincronizzaAltezzaRiga(riga.id);
        }

        break;
      }
      default:
        break;
    }
  }

  callSincronizzaAltezza() {
    this.sincronizzaAltezzaColonna();
    this.righe.forEach((r) => this.sincronizzaAltezzaRiga(r.id));

    this.variazioneDimensioneCelle.emit(true);
    /* this.onModificaTabella(this.salva()) */
  }

  /**
   * Nascondi prima colonna ( intestazioni righe)
   * @returns  true : nascondi / false : mostra
   */
  nascondiPrimaColonna(): boolean {
    if (this.isConfigurazione) {
      return this.righe.length === 0;
    }

    // Sono in compilazione della tabella
    return this.righe.every((r) => !r.label);
  }

  /**
   * Disabilito la configurazione dei totali se non esiste una colonna con un unità di misura
   * @returns
   */
  disabilitaTotali(): boolean {
    if (this.colonne.some((c) => c.unitaDiMisura?.id)) {
      return false;
    }
    this.totaleColonne = false;
    this.totaleRighe = false;
    return true;
  }

  /**
   * Metodo da chiamare dal componente padre (dal esterno)
   * @returns un oggetto di tipo tabella per il salvataggio
   */
  salva() {
    const tabella: TabellaDinamica = {
      id: this.tabella?.id ?? undefined,
      colonne: this.colonne,
      righe: this.righe,
      totaleRighe: this.totaleRighe,
      totaleColonne: this.totaleColonne,
      titoloTabella: this.titoloTabella,
    };

    return tabella;
  }

  public mostraELimina: boolean = false;

  mostraElimina(valore: boolean) {
    this.mostraELimina = valore;
    this.updateDisplayedColumns();
    this.callSincronizzaAltezza();
  }

  onInput(event: Event, colonna: Colonna, riga: Riga) {
    const input = event.target as HTMLTextAreaElement;
    let value = input.value;

    if (colonna.tipoColonna === 'UM') {
      // Accetta numeri e una singola virgola (o punto)
      // Sostituisce i punti con le virgole (opzionale) e rimuove tutto ciò che non è numero o virgola
      value = value.replace(/,/g, '.'); // Standardizza temporaneamente per gestire i decimali
      value = value.replace(/[^0-9.]/g, '');

      // Opzionale: limita a una sola virgola nel caso l'utente ne inserisca più di una
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }

      // Ripristina la virgola se preferisci il formato italiano
      value = value.replace('.', ',');
    }
    // aggiorna il campo visibile
    input.value = value;

    // sincronizzo l'oggetto
    if (!riga.celle[riga.id]) {
      riga.celle[riga.id] = {}; // creo l'oggetto se non esiste
    }
    riga.celle[riga.id][colonna.id] = value;
  }
}
